import {
  Component,
  Input,
  ViewContainerRef,
  EventEmitter,
  Output,
  ViewChild,
  forwardRef,
  OnInit,
  ElementRef, HostBinding
} from '@angular/core';
import { LMFormPanelBase } from './lm-form-panel-base';
import { LMListComponent, LMListTransactionEvent } from '@list/lm-list.component';
import { AppConstants } from '@utils/app.constants';
import { Observer, Observable, of } from 'rxjs';
import { LMPanelTransactionEvent } from '@form/services/lm-form-panel.service';
import { LMUrlInfoBuilderService } from '@services/lm-url-info-builder.service';
import { LMRefreshRequest } from "@app/components";

@Component({
  selector: 'lm-form-panel-list',
  template: `
    <lm-list
      *ngIf="activated"
      [listUrlInfo]="listUrlInfo"
      [listSpec]="listSpec"
      [fromDataView]="fromDataView"
      [specialActions]="specialActions"
      [formContextOnLeft]="formContextOnLeft"
      (listDisplayed)="onListDisplayed($event)"
      (contentModified)="onContentModified($event)"
      (updateToolbarStateEvent)="onUpdateToolbarStateEvent($event)"
      (sendContextMessageEvent)="onSendContextMessageEvent($event)"
      (specialActionToolbarButtonEvent)="onSpecialActionToolbarButtonEvent($event)"
      (listTransactionEvent)="onPanelTransactionEvent($event)"
    ></lm-list>
  `
})
export class LMFormPanelListComponent implements LMFormPanelBase, OnInit {
  @ViewChild(forwardRef(() => LMListComponent)) lmListComponent; // : LMListComponent;

  @HostBinding('style.display') display = 'block';
  @HostBinding('style.height') height = '100%';
  @HostBinding('class.implements-cart-view')  @Input() implementsCartView: boolean;

  @Input() listSpec: LMListSpec;
  @Input() placeHolder: ViewContainerRef;
  @Input() needsReload: boolean;
  @Input() panelDataState: LMPanelDataState;
  @Input() activated: boolean;
  @Input() panelId: string;
  @Input() specialActions: Array<LMBusActionReference>;
  @Input() formContextOnLeft: boolean;

  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView = fromDataView;
  }

  get fromDataView(): LMDataView {
    if (this.needsReload || (this.panelDataState === 'stale_needsReload')) {

      // -------------------------------------------------------------
      // return null fromDataView so tabs non activated tabs will
      // not load their data data.
      // -------------------------------------------------------------
      return null;
    }

    return this._fromDataView;
  }

  @Output() formPanelContentLoaded = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<any>();
  @Output() updateToolbarStateEvent = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() specialActionToolbarButtonEvent = new EventEmitter<LMSpecialActionToolbarButtonEvent>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();

  private _fromDataView: LMDataView = null;
  public listUrlInfo: LMListUrlInfo;

  constructor(
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.listUrlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(this.listSpec);
  }

  public getPanelHeight(): number {
    if (this.elementRef) {
      return this.elementRef.nativeElement.offsetHeight;
    }
    return 0;
  }

  public refresh(request?: LMRefreshRequest): Observable<boolean> {
    if (this.lmListComponent) {
      return this.lmListComponent.refresh(request);
    } else {
      return of (true);
    }
  }

  public save(callBack: Observer<boolean>, skipToast?: boolean) {
    if (this.lmListComponent && this.isDirty()) {
      this.lmListComponent.commitCellEdit();
      this.lmListComponent.runSaveAction(AppConstants.STANDARD_SAVE, callBack, skipToast);
    } else {
      callBack.next(true);
      callBack.complete();
    }
  }

  public isDirty(): boolean {
    return this.lmListComponent ? this.lmListComponent.isModified() : false;
  }

  onListDisplayed(event: any) {
    this.formPanelContentLoaded.emit(event);
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onUpdateToolbarStateEvent(event: any) {
    this.updateToolbarStateEvent.emit();
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onSpecialActionToolbarButtonEvent(event: LMSpecialActionToolbarButtonEvent) {
    this.specialActionToolbarButtonEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMListTransactionEvent) {
    const originatingPanel = this.panelId;
    const panelTransactionEvt = new LMPanelTransactionEvent(originatingPanel);
    panelTransactionEvt.originatingList = event?.eventSource?.listId;
    this.panelTransactionEvent.emit(panelTransactionEvt);
  }

  saveCompositeComponentState(): void {
    if (this.lmListComponent) {
      this.lmListComponent.saveComponentState();
    }
  }

  clearCompositeComponentState(): void {
    if (this.lmListComponent) {
      this.lmListComponent.clearComponentState();
    }
  }

  public commitCellEdit(): void {
    if (this.lmListComponent) {
      this.lmListComponent.commitCellEdit();
    }
  }

}
